import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import usePlan from '../../hooks/usePlan';
import {
  Search, Globe, MapPin, Star, Target, Share2,
  ChevronRight, Lock, CheckCircle, CheckCircle2, Clock, AlertTriangle,
  ArrowRight, Filter, Zap, MousePointerClick, BookOpen, Headphones,
  Copy, Sparkles, Loader2, Plug, KeyRound, UserCheck, XCircle, FileCheck,
  ExternalLink,
} from 'lucide-react';
import GuidedFlowModal, { hasGuidedFlow } from '../../components/platform/GuidedFlowModal';

const ACTIE_TYPES = {
  one_click: { icon: MousePointerClick, label: '1-Click', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', desc: 'Automatisch door ons platform' },
  guided: { icon: BookOpen, label: 'Guided', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', desc: 'U volgt stappen met onze hulp' },
  managed: { icon: Headphones, label: 'Door AI uitgevoerd', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', desc: 'Onze AI + team voeren dit automatisch voor u uit' },
};

/* Eerlijke execution status — matcht execution_engine */
const EXEC_STATUS = {
  completed:                { label: 'Uitgevoerd', color: '#15803d', bg: '#dcfce7', border: '#86efac', icon: CheckCircle2 },
  running:                  { label: 'Wordt nu uitgevoerd', color: '#1d4ed8', bg: '#dbeafe', border: '#93c5fd', icon: Loader2, spin: true },
  queued:                   { label: 'In wachtrij', color: '#a16207', bg: '#fef9c3', border: '#fde047', icon: Clock },
  awaiting_integration:     { label: 'Wacht op koppeling', color: '#0369a1', bg: '#e0f2fe', border: '#7dd3fc', icon: Plug },
  pending_api_approval:     { label: 'Wacht op API-goedkeuring', color: '#c2410c', bg: '#ffedd5', border: '#fdba74', icon: KeyRound },
  requires_customer_action: { label: 'Uw actie nodig', color: '#6d28d9', bg: '#ede9fe', border: '#c4b5fd', icon: UserCheck },
  team_handling:            { label: 'Ops team plaatst', color: '#0e7490', bg: '#cffafe', border: '#67e8f9', icon: Headphones },
  failed:                   { label: 'Mislukt', color: '#b91c1c', bg: '#fee2e2', border: '#fca5a5', icon: XCircle },
  no_executor:              { label: 'Handmatige verwerking', color: '#475569', bg: '#f1f5f9', border: '#cbd5e1', icon: FileCheck },
};

const ExecStatusBadge = ({ status, size = 'sm' }) => {
  const cfg = EXEC_STATUS[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  const padding = size === 'sm' ? '2px 7px' : '3px 9px';
  const fontSize = size === 'sm' ? '9px' : '10px';
  const iconSize = size === 'sm' ? 10 : 12;
  return (
    <span data-testid={`exec-status-${status}`} style={{
      display: 'inline-flex', alignItems: 'center', gap: '3px',
      padding, borderRadius: '6px', fontSize, fontWeight: 700,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
    }}>
      <Icon size={iconSize} style={cfg.spin ? { animation: 'spin 1s linear infinite' } : {}} />
      {cfg.label}
    </span>
  );
};

// AI-actie mapping: vertaalt opdracht.category + huidige_situatie naar de juiste AI endpoint.
// Returns null als geen AI-action geschikt is.
const aiActionFromOpdracht = (opdracht) => {
  const cat = (opdracht.category || '').toLowerCase();
  const titel = (opdracht.title || '').toLowerCase();
  const before = opdracht.huidige_situatie || '';
  if (cat === 'seo' || cat === 'website') {
    if (titel.includes('h1') || titel.includes('titel') || titel.includes('hero')) {
      return { type: 'rewrite_h1', label: 'Genereer betere H1 met AI', endpoint: '/api/ai/rewrite/h1', current_text: before, category: 'seo' };
    }
    if (titel.includes('meta') || titel.includes('description')) {
      return { type: 'rewrite_meta', label: 'Genereer betere meta description met AI', endpoint: '/api/ai/rewrite/meta', current_text: before, category: 'seo' };
    }
    if (titel.includes('cta') || titel.includes('call to action') || titel.includes('knop')) {
      return { type: 'rewrite_cta', label: 'Genereer betere CTA met AI', endpoint: '/api/ai/rewrite/cta', current_text: before, category: 'website' };
    }
  }
  if (cat === 'lokaal' || cat === 'gbp' || titel.includes('google business') || titel.includes('bedrijfsprofiel')) {
    return { type: 'rewrite_gbp', label: 'Genereer GBP omschrijving met AI', endpoint: '/api/ai/rewrite/gbp', current_text: before, category: 'gbp' };
  }
  if (cat === 'ads' || titel.includes('advertentie') || titel.includes('ads')) {
    return { type: 'rewrite_ad', label: 'Genereer ads copy met AI', endpoint: '/api/ai/rewrite/ad-copy', current_text: before, category: 'ads' };
  }
  return null;
};

const AiGenerateButton = ({ opdracht }) => {
  const ai = aiActionFromOpdracht(opdracht);
  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  if (!ai) return null;

  const generate = async () => {
    setLoading(true);
    try {
      // Lazy import van auth context binnen handler om geen circular dep te krijgen
      const token = localStorage.getItem('portal_token');
      const url = process.env.REACT_APP_BACKEND_URL + ai.endpoint;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          company: opdracht.company_name || '',
          branche: opdracht.branche || '',
          locatie: opdracht.locatie || '',
          current_text: ai.current_text,
          page_url: opdracht.pagina || 'homepage',
          extra_context: {},
        }),
      });
      const data = await resp.json();
      setResult(data);
    } catch (e) {
      setResult({ error: 'Fout — probeer het opnieuw.' });
    }
    setLoading(false);
  };

  const queueAction = async () => {
    if (!result) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('portal_token');
      const url = process.env.REACT_APP_BACKEND_URL + '/api/ai/actions';
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          opdracht_id: opdracht.id,
          action_type: ai.type,
          category: ai.category,
          title: opdracht.title || 'AI verbetering',
          description: opdracht.description || '',
          payload: {
            before: ai.current_text,
            after: result.text || result.h1 || result.description || result.cta || result.suggestion || JSON.stringify(result).slice(0, 200),
            page_url: opdracht.pagina || 'homepage',
            change_type: ai.type,
          },
        }),
      });
    } catch { /* ignore */ }
    setLoading(false);
    // Toast via window event (parent gebruikt sonner)
    window.dispatchEvent(new CustomEvent('imperia-ai:toast', { detail: { type: 'success', msg: 'Actie toegevoegd aan AI Acties' } }));
  };

  const suggestion = result?.text || result?.h1 || result?.description || result?.cta || result?.suggestion;

  return (
    <div data-testid="ai-generate-block" style={{ marginBottom: '16px', padding: '12px 14px', background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)', borderRadius: '10px' }}>
      {!result ? (
        <button
          data-testid="ai-generate-btn"
          onClick={generate}
          disabled={loading}
          style={{ background: '#D4A017', color: '#0f172a', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
        >
          <Sparkles size={12} /> {loading ? 'Genereren...' : ai.label}
        </button>
      ) : (
        <div>
          <p style={{ fontSize: '10px', color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: '6px' }}>
            AI suggestie
          </p>
          {suggestion ? (
            <p style={{ fontSize: '12px', color: 'white', lineHeight: 1.5, marginBottom: '8px' }}>{suggestion}</p>
          ) : result.error ? (
            <p style={{ fontSize: '11px', color: '#fda4af' }}>{result.error}</p>
          ) : (
            <pre style={{ fontSize: '10px', color: 'white', whiteSpace: 'pre-wrap', maxHeight: '120px', overflow: 'auto' }}>{JSON.stringify(result, null, 2)}</pre>
          )}
          {suggestion && (
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                data-testid="ai-queue-btn"
                onClick={queueAction}
                disabled={loading}
                style={{ background: '#D4A017', color: '#0f172a', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
              >
                Voeg toe aan AI acties
              </button>
              <button
                onClick={generate}
                disabled={loading}
                style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '6px', padding: '6px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
              >
                Opnieuw
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};



const ActieTypeBadge = ({ type, size = 'md' }) => {
  const cfg = ACTIE_TYPES[type];
  if (!cfg) return null;
  const Icon = cfg.icon;
  const isSm = size === 'sm';
  return (
    <span data-testid={`actie-badge-${type}`} style={{
      display: 'inline-flex', alignItems: 'center', gap: isSm ? '3px' : '5px',
      padding: isSm ? '2px 6px' : '3px 8px', borderRadius: '6px', fontSize: isSm ? '9px' : '10px',
      fontWeight: 700, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      whiteSpace: 'nowrap',
    }}>
      <Icon size={isSm ? 10 : 12} />
      {cfg.label}
    </span>
  );
};

function DiyExampleCard({ example }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(example.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = example.text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div data-testid="diy-example-card" style={{
      background: 'white', borderRadius: '12px', border: '1px solid #d1fae5',
      padding: '14px', marginBottom: '10px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#059669' }}>
          {example.label}
        </span>
        <button
          data-testid="copy-example-btn"
          onClick={handleCopy}
          style={{
            background: copied ? '#059669' : '#ecfdf5', border: `1px solid ${copied ? '#059669' : '#a7f3d0'}`,
            borderRadius: '6px', padding: '3px 8px', fontSize: '10px', fontWeight: 600,
            color: copied ? 'white' : '#059669', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0,
            transition: 'all 0.2s ease',
          }}
        >
          {copied ? <><CheckCircle size={10} /> Gekopieerd</> : <><Copy size={10} /> Kopieer</>}
        </button>
      </div>
      <div style={{
        background: '#f0fdf4', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px',
        fontFamily: "'Sora', monospace", fontSize: '12px', color: '#0f172a', lineHeight: 1.6,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        border: '1px dashed #a7f3d0',
      }}>
        {example.text}
      </div>
      {example.where && (
        <div style={{
          display: 'flex', gap: '6px', alignItems: 'flex-start',
        }}>
          <ArrowRight size={12} style={{ color: '#94a3b8', flexShrink: 0, marginTop: '2px' }} />
          <p style={{ fontSize: '11px', color: '#64748b', lineHeight: 1.5 }}>
            {example.where}
          </p>
        </div>
      )}
    </div>
  );
}

const CATEGORIES = [
  { id: '', label: 'Alle', icon: null },
  { id: 'seo', label: 'SEO', icon: Search, color: '#059669' },
  { id: 'website', label: 'Website', icon: Globe, color: '#3b82f6' },
  { id: 'lokaal', label: 'Lokaal', icon: MapPin, color: '#D4A017' },
  { id: 'reputatie', label: 'Reputatie', icon: Star, color: '#8b5cf6' },
  { id: 'advertenties', label: 'Advertenties', icon: Target, color: '#ef4444' },
  { id: 'social', label: 'Social', icon: Share2, color: '#0ea5e9' },
];

const PRIORITY_COLORS = {
  kritiek: { bg: '#fef2f2', border: '#fca5a5', text: '#dc2626', label: 'Kritiek' },
  hoog: { bg: '#fff7ed', border: '#fdba74', text: '#ea580c', label: 'Hoog' },
  middel: { bg: '#fefce8', border: '#fde047', text: '#ca8a04', label: 'Middel' },
  laag: { bg: '#f0fdf4', border: '#86efac', text: '#16a34a', label: 'Laag' },
};

const CAT_COLORS = {
  seo: '#059669', website: '#3b82f6', lokaal: '#D4A017',
  reputatie: '#8b5cf6', advertenties: '#ef4444', social: '#0ea5e9',
};

function OpdrachtCard({ opdracht, locked, isManaged, onOpenDetail }) {
  const prio = PRIORITY_COLORS[opdracht.priority] || PRIORITY_COLORS.middel;
  const catColor = CAT_COLORS[opdracht.category] || '#64748b';
  const isDone = opdracht.status === 'done';
  // Voor managed users: forceer actie_type=managed zodat badge + CTA's kloppen
  const effectiveActieType = isManaged ? 'managed' : opdracht.actie_type;

  return (
    <div
      data-testid={`opdracht-card-${opdracht.template_id}`}
      onClick={() => !locked && onOpenDetail(opdracht)}
      style={{
        background: isDone ? '#f8fdf8' : 'white',
        borderRadius: '14px',
        border: `1px solid ${isDone ? '#86efac' : '#f1f5f9'}`,
        padding: '20px',
        cursor: locked ? 'default' : 'pointer',
        transition: 'all 0.2s',
        position: 'relative',
        overflow: 'hidden',
        opacity: isDone ? 0.7 : 1,
      }}
      onMouseEnter={e => { if (!locked) { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
    >
      {locked && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(3px)', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: '14px',
        }}>
          <Lock size={20} style={{ color: '#94a3b8', marginBottom: '6px' }} />
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Upgrade naar Radar</span>
          <Link to="/platform/checkout" data-testid="upgrade-cta-locked" style={{
            marginTop: '8px', background: '#D4A017', color: '#0f172a', padding: '6px 14px',
            borderRadius: '8px', fontSize: '11px', fontWeight: 700, textDecoration: 'none',
          }}>
            Ontgrendel
          </Link>
        </div>
      )}

      {/* Top row: priority + category + actie_type */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <span style={{
          padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 700,
          background: prio.bg, color: prio.text, border: `1px solid ${prio.border}`,
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          {prio.label}
        </span>
        <span style={{
          padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 600,
          background: `${catColor}10`, color: catColor,
          textTransform: 'capitalize',
        }}>
          {opdracht.category}
        </span>
        {isManaged && opdracht.execution_status ? (
          <ExecStatusBadge status={opdracht.execution_status} size="sm" />
        ) : (
          opdracht.actie_type && <ActieTypeBadge type={effectiveActieType} size="sm" />
        )}
        {isDone && (
          <CheckCircle size={14} style={{ color: '#22c55e', marginLeft: 'auto' }} />
        )}
      </div>

      {/* Title */}
      <h3 style={{
        fontFamily: "'Sora', sans-serif", fontSize: '15px', fontWeight: 700,
        color: '#0f172a', marginBottom: '6px', lineHeight: 1.4,
      }}>
        {opdracht.title}
      </h3>

      {/* Pagina chip + actie type */}
      {opdracht.pagina && (
        <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: '10px' }}>
          📍 {opdracht.pagina}
        </p>
      )}

      {/* Huidig → Beter blok */}
      {(opdracht.huidige_situatie || opdracht.beter_voorstel) ? (
        <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {opdracht.huidige_situatie && (
            <div data-testid="opdracht-huidig-block" style={{
              background: '#fef2f2', borderLeft: '3px solid #f87171', borderRadius: '0 8px 8px 0',
              padding: '8px 10px',
            }}>
              <p style={{ fontSize: '9px', color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: '2px' }}>
                Huidig op uw site
              </p>
              <p style={{ fontSize: '12px', color: '#0f172a', fontWeight: 500, fontStyle: 'italic', lineHeight: 1.4 }}>
                &ldquo;{(opdracht.huidige_situatie || '').slice(0, 140)}{opdracht.huidige_situatie?.length > 140 ? '…' : ''}&rdquo;
              </p>
            </div>
          )}
          {opdracht.beter_voorstel && (
            <div data-testid="opdracht-beter-block" style={{
              background: '#fff8e1', borderLeft: '3px solid #D4A017', borderRadius: '0 8px 8px 0',
              padding: '8px 10px',
            }}>
              <p style={{ fontSize: '9px', color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: '2px' }}>
                Beter voorstel
              </p>
              <p style={{ fontSize: '12px', color: '#0f172a', fontWeight: 600, lineHeight: 1.4 }}>
                {(opdracht.beter_voorstel || '').slice(0, 160)}{opdracht.beter_voorstel?.length > 160 ? '…' : ''}
              </p>
            </div>
          )}
        </div>
      ) : (
        /* Fallback: oude description */
        <p style={{
          color: '#64748b', fontSize: '13px', lineHeight: 1.6, marginBottom: '14px',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {opdracht.description}
        </p>
      )}

      {/* Impact + Time */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Zap size={12} style={{ color: '#D4A017' }} />
          <span style={{ fontSize: '11px', color: '#64748b' }}>{opdracht.impact}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Clock size={12} style={{ color: '#94a3b8' }} />
          <span style={{ fontSize: '11px', color: '#94a3b8' }}>{opdracht.diy_time}</span>
        </div>
      </div>

      {/* CTAs */}
      <div style={{ display: 'flex', gap: '8px' }}>
        {isManaged ? (
          <button
            data-testid={`opdracht-status-${opdracht.template_id}`}
            onClick={e => { e.stopPropagation(); onOpenDetail(opdracht); }}
            style={{
              flex: 1, padding: '9px 12px', borderRadius: '8px', fontSize: '12px',
              fontWeight: 700, border: 'none', background: '#7c3aed',
              color: 'white', cursor: 'pointer', transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
            }}
          >
            <Headphones size={13} /> Status & voortgang
          </button>
        ) : opdracht.actie_type === 'guided' && hasGuidedFlow(opdracht.template_id) ? (
          <button
            data-testid={`opdracht-guided-${opdracht.template_id}`}
            onClick={e => { e.stopPropagation(); onOpenDetail(opdracht, 'guided'); }}
            style={{
              flex: 1, padding: '9px 12px', borderRadius: '8px', fontSize: '12px',
              fontWeight: 700, border: 'none', background: '#2563eb',
              color: 'white', cursor: 'pointer', transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
            }}
          >
            <Sparkles size={13} /> Start Guided Setup
          </button>
        ) : (
          <button
            data-testid={`opdracht-zelf-doen-${opdracht.template_id}`}
            onClick={e => { e.stopPropagation(); onOpenDetail(opdracht); }}
            style={{
              flex: 1, padding: '9px 12px', borderRadius: '8px', fontSize: '12px',
              fontWeight: 600, border: '1px solid #e2e8f0', background: 'white',
              color: '#0f172a', cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#D4A017'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
          >
            Zelf doen
          </button>
        )}
      </div>
    </div>
  );
}

function OpdrachtDetailModal({ opdracht, onClose, onOrder, isManaged }) {
  const [tab, setTab] = useState(isManaged ? 'managed' : 'zelf');
  const prio = PRIORITY_COLORS[opdracht.priority] || PRIORITY_COLORS.middel;
  const effectiveActieType = isManaged ? 'managed' : opdracht.actie_type;
  const actieInfo = ACTIE_TYPES[effectiveActieType];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div style={{
        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(4px)',
      }} onClick={onClose} />
      <div data-testid="opdracht-detail-modal" style={{
        position: 'relative', background: 'white', borderRadius: '18px',
        maxWidth: '600px', width: '100%', maxHeight: '85vh', overflow: 'auto',
        boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{ padding: '24px 24px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <span style={{
              padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 700,
              background: prio.bg, color: prio.text, border: `1px solid ${prio.border}`,
              textTransform: 'uppercase',
            }}>
              {prio.label} prioriteit
            </span>
            <span style={{
              padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 600,
              background: `${CAT_COLORS[opdracht.category] || '#64748b'}10`,
              color: CAT_COLORS[opdracht.category] || '#64748b',
              textTransform: 'capitalize',
            }}>
              {opdracht.category}
            </span>
            {isManaged && opdracht.execution_status ? (
              <ExecStatusBadge status={opdracht.execution_status} size="md" />
            ) : (
              opdracht.actie_type && <ActieTypeBadge type={effectiveActieType} />
            )}
            <button onClick={onClose} style={{
              marginLeft: 'auto', background: 'none', border: 'none', fontSize: '20px',
              cursor: 'pointer', color: '#94a3b8', lineHeight: 1,
            }}>x</button>
          </div>
          <h2 style={{
            fontFamily: "'Sora', sans-serif", fontSize: '20px', fontWeight: 800,
            color: '#0f172a', marginBottom: '8px',
          }}>
            {opdracht.title}
          </h2>
          <p style={{ color: '#64748b', fontSize: '14px', lineHeight: 1.6, marginBottom: '16px' }}>
            {opdracht.description}
          </p>

          {/* Huidig → Beter detail blok */}
          {(opdracht.huidige_situatie || opdracht.beter_voorstel) && (
            <div data-testid="opdracht-detail-huidig-beter" style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {opdracht.pagina && (
                <p style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                  📍 Pagina: {opdracht.pagina}
                </p>
              )}
              {opdracht.huidige_situatie && (
                <div style={{ background: '#fef2f2', borderLeft: '4px solid #f87171', borderRadius: '0 10px 10px 0', padding: '12px 16px' }}>
                  <p style={{ fontSize: '10px', color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: '4px' }}>Huidig op uw site</p>
                  <p style={{ fontSize: '13px', color: '#0f172a', fontWeight: 500, fontStyle: 'italic', lineHeight: 1.5 }}>&ldquo;{opdracht.huidige_situatie}&rdquo;</p>
                </div>
              )}
              {opdracht.beter_voorstel && (
                <div style={{ background: '#fff8e1', borderLeft: '4px solid #D4A017', borderRadius: '0 10px 10px 0', padding: '12px 16px' }}>
                  <p style={{ fontSize: '10px', color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: '4px' }}>Beter voorstel</p>
                  <p style={{ fontSize: '13px', color: '#0f172a', fontWeight: 600, lineHeight: 1.5 }}>{opdracht.beter_voorstel}</p>
                  {opdracht.waarom_beter && (
                    <p style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', lineHeight: 1.5 }}>
                      <strong>Waarom beter:</strong> {opdracht.waarom_beter}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Genereer met AI knop */}
          <AiGenerateButton opdracht={opdracht} />

          {/* Actie type uitleg — verbergen voor managed users (eerlijke status staat in panel) */}
          {actieInfo && !isManaged && (
            <div data-testid="actie-type-detail" style={{
              background: actieInfo.bg, borderRadius: '10px', padding: '12px 14px',
              display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px',
              border: `1px solid ${actieInfo.border}`,
            }}>
              <actieInfo.icon size={18} style={{ color: actieInfo.color, flexShrink: 0 }} />
              <div>
                <span style={{ fontSize: '12px', fontWeight: 700, color: actieInfo.color }}>
                  {actieInfo.label}
                </span>
                <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '6px' }}>
                  — {actieInfo.desc}
                </span>
              </div>
            </div>
          )}

          {/* Impact bar */}
          <div style={{
            background: '#f8fafc', borderRadius: '10px', padding: '12px 14px',
            display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px',
          }}>
            <AlertTriangle size={16} style={{ color: '#D4A017', flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
              {opdracht.impact}
            </span>
          </div>

          {/* Tab switcher — voor managed users alleen "Voortgang" tab; anderen: zelf / team */}
          {isManaged ? (
            <div style={{ display: 'flex', gap: '4px', marginBottom: '0' }}>
              <button
                data-testid="tab-managed-status"
                style={{
                  flex: 1, padding: '12px', borderRadius: '10px 10px 0 0', border: 'none',
                  background: '#f5f3ff', cursor: 'default',
                  fontSize: '13px', fontWeight: 700, color: '#7c3aed',
                  borderBottom: '2px solid #7c3aed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}
              >
                <Headphones size={13} /> Status & voortgang
              </button>
            </div>
          ) : (
          <div style={{ display: 'flex', gap: '4px', marginBottom: '0' }}>
            <button
              data-testid="tab-zelf-doen"
              onClick={() => setTab('zelf')}
              style={{
                flex: 1, padding: '12px', borderRadius: '10px 10px 0 0', border: 'none',
                background: tab === 'zelf' ? '#f8fafc' : 'transparent', cursor: 'pointer',
                fontSize: '13px', fontWeight: tab === 'zelf' ? 700 : 500,
                color: tab === 'zelf' ? '#0f172a' : '#94a3b8',
                borderBottom: tab === 'zelf' ? '2px solid #D4A017' : '2px solid transparent',
              }}
            >
              Zelf doen
            </button>
            <button
              data-testid="tab-door-imperia-ai"
              onClick={() => setTab('team')}
              style={{
                flex: 1, padding: '12px', borderRadius: '10px 10px 0 0', border: 'none',
                background: tab === 'team' ? '#f8fafc' : 'transparent', cursor: 'pointer',
                fontSize: '13px', fontWeight: tab === 'team' ? 700 : 500,
                color: tab === 'team' ? '#0f172a' : '#94a3b8',
                borderBottom: tab === 'team' ? '2px solid #D4A017' : '2px solid transparent',
              }}
            >
              Laat IMPERIA AI het regelen
            </button>
          </div>
          )}
        </div>

        {/* Tab content */}
        <div style={{ padding: '20px 24px 24px', background: '#f8fafc', borderRadius: '0 0 18px 18px' }}>
          {isManaged ? (
            <div data-testid="managed-status-panel">
              {(() => {
                const execStatus = opdracht.execution_status;
                const execMeta = execStatus ? EXEC_STATUS[execStatus] : null;
                const ExecIcon = execMeta?.icon;
                return (
                  <div style={{
                    background: 'white', borderRadius: '12px', padding: '18px',
                    border: `1px solid ${execMeta?.border || '#f1f5f9'}`, marginBottom: '12px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      {ExecIcon ? (
                        <ExecIcon
                          size={16}
                          style={{
                            color: execMeta.color,
                            ...(execMeta.spin ? { animation: 'spin 1s linear infinite' } : {}),
                          }}
                        />
                      ) : (
                        <span style={{
                          width: '8px', height: '8px', borderRadius: '50%',
                          background: opdracht.status === 'done' ? '#22c55e' : '#94a3b8',
                        }} />
                      )}
                      <p style={{
                        fontSize: '12px',
                        color: execMeta?.color || '#94a3b8',
                        textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700,
                      }}>
                        {execMeta?.label || (opdracht.status === 'done' ? 'Voltooid' : 'Wordt ingepland')}
                      </p>
                    </div>
                    <p style={{ fontSize: '13px', color: '#0f172a', lineHeight: 1.6, marginBottom: 10 }}>
                      {opdracht.execution_reason
                        || 'Deze opdracht wordt door uw Managed AI ingepland. Voortgang ziet u op de AI Acties pagina.'}
                    </p>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {opdracht.execution_evidence_url && execStatus === 'completed' && (
                        <a
                          data-testid="opdracht-evidence-link"
                          href={opdracht.execution_evidence_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: '#15803d', color: 'white', padding: '6px 12px',
                            borderRadius: 8, fontSize: 11, fontWeight: 700, textDecoration: 'none',
                          }}>
                          <ExternalLink size={12} /> Bekijk live resultaat
                        </a>
                      )}
                      <Link to="/platform/ai-acties" style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: '#7c3aed', color: 'white', padding: '6px 12px',
                        borderRadius: 8, fontSize: 11, fontWeight: 700, textDecoration: 'none',
                      }}>
                        Bekijk AI Acties <ArrowRight size={12} />
                      </Link>
                    </div>
                  </div>
                );
              })()}
              <p style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center' }}>
                Eerlijk: deze status reflecteert wat onze AI op dit moment voor u kan uitvoeren — géén nep-progressie.
              </p>
            </div>
          ) : tab === 'zelf' ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Clock size={14} style={{ color: '#94a3b8' }} />
                <p style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                  Geschatte tijd: {opdracht.diy_time}
                </p>
              </div>

              {/* Concrete voorbeeldteksten */}
              {(opdracht.diy_examples || []).length > 0 && (
                <div style={{ marginBottom: '18px' }}>
                  <p style={{
                    fontSize: '12px', fontWeight: 700, color: '#059669', textTransform: 'uppercase',
                    letterSpacing: '0.04em', marginBottom: '10px',
                  }}>
                    Klaar om te gebruiken
                  </p>
                  {opdracht.diy_examples.map((ex, i) => (
                    <DiyExampleCard key={i} example={ex} />
                  ))}
                </div>
              )}

              {/* Stappenplan */}
              <p style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px' }}>
                Stappenplan
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {(opdracht.diy_steps || []).map((step, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: '10px', background: 'white', padding: '12px',
                    borderRadius: '10px', border: '1px solid #f1f5f9',
                  }}>
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                      background: '#0f172a', color: '#D4A017', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: 700,
                    }}>
                      {i + 1}
                    </div>
                    <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.5 }}>{step}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div style={{
                background: 'white', borderRadius: '12px', padding: '20px',
                border: '1px solid #f1f5f9', marginBottom: '16px',
              }}>
                <p style={{ fontSize: '11px', color: '#D4A017', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                  IMPERIA AI Beheer
                </p>
                <h3 style={{ fontFamily: "'Sora', sans-serif", fontSize: '16px', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
                  Laat alles regelen door IMPERIA AI
                </h3>
                <p style={{ color: '#64748b', fontSize: '13px', lineHeight: 1.6, marginBottom: '14px' }}>
                  Wij lossen dit en al uw andere verbeterpunten professioneel op. Dagelijks en wekelijks beheren wij uw SEO, advertenties, content en meer. U hoeft zelf niets te doen.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                  {['Alle opdrachten worden uitgevoerd', 'Dagelijks/wekelijks beheer', 'SEO, Ads, Content & meer', 'Maandelijkse rapportage', 'Dedicated accountmanager'].map(b => (
                    <span key={b} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#059669' }}>
                      <CheckCircle size={13} /> {b}
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '16px' }}>
                  <span style={{ fontFamily: "'Sora', sans-serif", fontSize: '28px', fontWeight: 800, color: '#0f172a' }}>
                    €995,95
                  </span>
                  <span style={{ fontSize: '13px', color: '#94a3b8' }}>/maand</span>
                </div>
                <button
                  data-testid="order-beheer-btn"
                  onClick={() => onOrder(opdracht)}
                  style={{
                    width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                    background: '#0f172a', color: 'white', fontSize: '14px', fontWeight: 700,
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: '8px',
                  }}
                >
                  Start IMPERIA AI Beheer <ArrowRight size={16} />
                </button>
              </div>
              <p style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center' }}>
                Na bestelling nemen wij binnen 24 uur contact met u op.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OpdrachtenPage() {
  const { api, user } = useAuth();
  const plan = usePlan();
  const isManaged = plan.isManaged;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [selectedOpdracht, setSelectedOpdracht] = useState(null);
  const [guidedOpdracht, setGuidedOpdracht] = useState(null);
  const [ordering, setOrdering] = useState(false);

  useEffect(() => {
    api('get', '/api/platform/opdrachten')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleOrder = async (opdracht) => {
    setOrdering(true);
    try {
      const originUrl = window.location.origin;
      const r = await api('post', '/api/checkout/create-session', {
        package_id: 'imperium_beheer',
        origin_url: originUrl,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
        return;
      }
    } catch (err) {
      console.error(err);
    }
    setOrdering(false);
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: '32px', height: '32px', border: '3px solid #f1f5f9', borderTopColor: '#D4A017', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  const isPaid = data?.is_paid || plan.isPaid;
  const opdrachten = data?.opdrachten || [];
  const FREE_LIMIT = 6; // 3 kritieke + 3 quick wins (zoals beloofd op prijspagina)

  const filtered = activeCategory
    ? opdrachten.filter(o => o.category === activeCategory)
    : opdrachten;

  const stats = {
    total: opdrachten.length,
    hoog: opdrachten.filter(o => o.priority === 'hoog' || o.priority === 'kritiek').length,
    done: opdrachten.filter(o => o.status === 'done').length,
  };

  return (
    <div data-testid="opdrachten-page">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{
          fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800,
          color: '#0f172a', marginBottom: '4px',
        }}>
          Uw Opdrachten
        </h1>
        <p style={{ color: '#64748b', fontSize: '14px' }}>
          Verbeter uw online aanwezigheid stap voor stap. Los ze zelf op of laat het door IMPERIA AI doen.
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Totaal', value: stats.total, color: '#0f172a' },
          { label: 'Hoge prioriteit', value: stats.hoog, color: '#ef4444' },
          { label: 'Afgerond', value: stats.done, color: '#22c55e' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'white', borderRadius: '12px', padding: '16px',
            border: '1px solid #f1f5f9', textAlign: 'center',
          }}>
            <p style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800, color: s.color }}>
              {s.value}
            </p>
            <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Category filter */}
      <div style={{
        display: 'flex', gap: '6px', marginBottom: '20px', overflowX: 'auto',
        paddingBottom: '4px',
      }}>
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            data-testid={`filter-${cat.id || 'alle'}`}
            onClick={() => setActiveCategory(cat.id)}
            style={{
              padding: '8px 14px', borderRadius: '20px', border: 'none',
              background: activeCategory === cat.id ? '#0f172a' : '#f1f5f9',
              color: activeCategory === cat.id ? 'white' : '#64748b',
              fontSize: '12px', fontWeight: 600, cursor: 'pointer',
              whiteSpace: 'nowrap', transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', gap: '5px',
            }}
          >
            {cat.icon && <cat.icon size={13} />}
            {cat.label}
          </button>
        ))}
      </div>

      {/* Opdrachten grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px' }}>
        {filtered.map((opdracht, idx) => (
          <OpdrachtCard
            key={opdracht.id}
            opdracht={opdracht}
            locked={!isPaid && !isManaged && idx >= FREE_LIMIT}
            isManaged={isManaged}
            onOpenDetail={(o, mode) => {
              if (mode === 'guided') setGuidedOpdracht(o);
              else setSelectedOpdracht(o);
            }}
          />
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '48px 20px', color: '#94a3b8',
        }}>
          <Search size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
          <p style={{ fontSize: '14px' }}>Geen opdrachten gevonden in deze categorie.</p>
        </div>
      )}

      {/* Upgrade banner — alleen voor free users die NIET premium zijn */}
      {!isPaid && !isManaged && opdrachten.length > FREE_LIMIT && (
        <div style={{
          marginTop: '24px', background: 'linear-gradient(135deg, #0f172a, #1e1b4b)',
          borderRadius: '14px', padding: '24px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px',
        }}>
          <div>
            <p style={{ color: '#D4A017', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
              IMPERIA AI Radar
            </p>
            <p style={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>
              Ontgrendel alle {opdrachten.length} opdrachten
            </p>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '2px' }}>
              €14,95/mnd - Volledige toegang - Altijd opzegbaar
            </p>
          </div>
          <Link to="/platform/checkout" data-testid="upgrade-banner-cta" style={{
            background: 'linear-gradient(135deg, #D4A017, #f5c842)', color: '#0f172a',
            fontWeight: 700, padding: '12px 24px', borderRadius: '10px',
            textDecoration: 'none', fontSize: '13px',
          }}>
            Upgrade nu <ArrowRight size={14} style={{ display: 'inline', marginLeft: '4px' }} />
          </Link>
        </div>
      )}

      {/* Detail modal */}
      {selectedOpdracht && (
        <OpdrachtDetailModal
          opdracht={selectedOpdracht}
          onClose={() => setSelectedOpdracht(null)}
          onOrder={handleOrder}
          isManaged={isManaged}
        />
      )}

      {/* Guided flow modal */}
      {guidedOpdracht && (
        <GuidedFlowModal
          opdracht={guidedOpdracht}
          onClose={() => setGuidedOpdracht(null)}
        />
      )}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
